import classNames from 'classnames';
import Typography from '@/components/atoms/typography/typography';
import styles from './price-display.module.scss';

type PriceDisplayVariant = 'primary' | 'secondary';

export interface PriceDisplayProps {
  /** The type of the splash */
  $type?: Content.Components['SearchByThemeCardSplashDto']['$type'];
  /** The text to show as an additional tag */
  additionalTag?: string;
  /** The variant of the additional tag */
  additionalTagVariant?: Content.Components['AdditionalTagTheme'];
  /** The class name to apply */
  className?: string;
  /** The text to show as a strikethrough text, next to the sub headline */
  discountHeadline?: Content.Components['SearchByThemeCardPriceSplashDto']['discountHeadline'];
  /** The text to show as a headline */
  primaryHeadline: Content.Components['SearchByThemeCardSplashDto']['primaryHeadline'];
  /** The text to show as a sub headline */
  secondaryHeadline?: Content.Components['SearchByThemeCardSplashDto']['secondaryHeadline'];
  /** The text to show as a tertiary headline */
  tertiaryHeadline?: Content.Components['SearchByThemeCardSplashDto']['tertiaryHeadline'];
  /** The variant of the accent */
  variant?: PriceDisplayVariant;
}

const variants: Record<PriceDisplayVariant, string> = {
  primary: styles.isPrimary,
  secondary: styles.isSecondary,
};

const additionalTagVariants: Record<
  Content.Components['AdditionalTagTheme'],
  string
> = {
  blackweek: styles.isBlackWeek,
  primary: styles.isPrimary,
};

const PriceDisplay = ({
  $type,
  additionalTag,
  additionalTagVariant,
  className,
  primaryHeadline,
  discountHeadline,
  secondaryHeadline,
  tertiaryHeadline,
  variant = 'primary',
}: PriceDisplayProps) => {
  const hasAdditionalTag = !!additionalTag;
  const isPrimaryAdditionalTag = additionalTagVariant === 'primary';

  return (
    <div
      className={classNames(styles.component, className, variants[variant], {
        [styles.isDiscount]: $type === 'SearchByThemeCardPriceSplashDto',
      })}
    >
      <div
        className={classNames(styles.mainWrapper, {
          [styles.hasPrimaryAdditionalTag]: isPrimaryAdditionalTag,
        })}
      >
        {primaryHeadline && (
          <Typography tagStyle="priceMedium">{primaryHeadline}</Typography>
        )}

        {(secondaryHeadline || discountHeadline) && (
          <div className={styles.secondaryHeadline}>
            {secondaryHeadline && (
              <Typography tagStyle="bodyMediumBold">
                {secondaryHeadline}
              </Typography>
            )}

            {discountHeadline && (
              <Typography
                className={styles.strikeThrough}
                tagStyle="labelSmall"
              >
                {discountHeadline}
              </Typography>
            )}
          </div>
        )}

        {tertiaryHeadline && (
          <Typography tagStyle="bodyMediumBold">{tertiaryHeadline}</Typography>
        )}
      </div>

      {hasAdditionalTag && (
        <div
          className={classNames(
            styles.additionalWrapper,
            additionalTagVariant && additionalTagVariants[additionalTagVariant],
          )}
        >
          <Typography className={styles.additionalTag} tagStyle="labelSmall">
            {additionalTag}
          </Typography>
        </div>
      )}
    </div>
  );
};

PriceDisplay.displayName = 'PriceDisplay';

export default PriceDisplay;
